/**
 * Dashboard — default post-login landing page.
 * Shows org overview, quick stats, services, and quick actions.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { config } from '../config';
import LoadingSpinner from '../components/LoadingSpinner';

interface ServiceInfo {
  id: string;
  name: string;
}

interface DashboardStats {
  services: number;
  devices: number;
  members: number;
  agentTokens: number;
}

function roleBadgeClass(role: string): string {
  switch (role) {
    case 'owner': return 'bg-[var(--gold-tint,#262620)] text-[var(--accent-gold,#D4A73C)]';
    case 'admin': return 'bg-[var(--accent-tint,#142330)] text-[var(--accent,#3D8FBF)]';
    default: return 'bg-[var(--surface-700,#1A2530)] text-[var(--content-secondary,#94A3B8)]';
  }
}

export default function Apps() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading, currentOrganization, organizations } = useAuth();
  const [services, setServices] = useState<ServiceInfo[]>([]);
  const [stats, setStats] = useState<DashboardStats>({ services: 0, devices: 0, members: 0, agentTokens: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const params = new URLSearchParams(location.search);
  const showWelcome = params.get('welcome') === '1';

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
    if (!authLoading && isAuthenticated && organizations.length === 0) {
      navigate('/onboarding', { replace: true });
    }
  }, [authLoading, isAuthenticated, organizations, navigate]);

  useEffect(() => {
    if (!currentOrganization) return;

    setLoading(true);
    setError(null);

    const orgId = currentOrganization.id;

    // Fetch services and stats in parallel
    Promise.all([
      fetch(`${config.gatewayUrl}/v1/organizations/${orgId}/services`, { credentials: 'include' })
        .then((r) => r.ok ? r.json() : { services: [] })
        .then((data) => {
          const svcList = data.services || [];
          setServices(svcList);
          return svcList.length;
        }),
      fetch(`${config.gatewayUrl}/v1/organizations/${orgId}/devices`, { credentials: 'include' })
        .then((r) => r.ok ? r.json() : { devices: [] })
        .then((data) => (data.devices || []).length)
        .catch(() => 0),
      fetch(`${config.gatewayUrl}/v1/agent-tokens`, { credentials: 'include' })
        .then((r) => r.ok ? r.json() : [])
        .then((data) => (Array.isArray(data) ? data : []).filter((t: { active?: boolean }) => t.active).length)
        .catch(() => 0),
    ])
      .then(([svcCount, deviceCount, tokenCount]) => {
        setStats({
          services: svcCount,
          devices: deviceCount,
          members: 0, // filled below if manager
          agentTokens: tokenCount,
        });
      })
      .catch(() => setError('Failed to load dashboard.'))
      .finally(() => setLoading(false));
  }, [currentOrganization]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  const isOrgManager = currentOrganization?.role === 'owner' || currentOrganization?.role === 'admin';

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Welcome banner (post-invitation-accept) */}
      {showWelcome && currentOrganization && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-5 py-4 flex items-start space-x-3">
          <svg className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="font-medium text-green-800 dark:text-green-300">
              You've joined {currentOrganization.name}
            </p>
          </div>
        </div>
      )}

      {/* Org header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-content-primary">Dashboard</h1>
          {currentOrganization && (
            <div className="flex items-center space-x-2 mt-1">
              <span className="text-sm text-content-secondary">{currentOrganization.name}</span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${roleBadgeClass(currentOrganization.role)}`}>
                {currentOrganization.role}
              </span>
            </div>
          )}
        </div>
        {isOrgManager && (
          <Link
            to="/org"
            className="btn-primary text-sm flex items-center space-x-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>Manage Org</span>
          </Link>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner text="Loading dashboard..." />
        </div>
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      ) : (
        <>
          {/* Stats cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <div className="card text-center py-4">
              <div className="text-2xl font-bold text-content-primary">{stats.services}</div>
              <div className="text-xs text-content-tertiary mt-1">Services</div>
            </div>
            <div className="card text-center py-4">
              <div className="text-2xl font-bold text-content-primary">{stats.devices}</div>
              <div className="text-xs text-content-tertiary mt-1">Devices</div>
            </div>
            <div className="card text-center py-4">
              <div className="text-2xl font-bold text-content-primary">{stats.agentTokens}</div>
              <div className="text-xs text-content-tertiary mt-1">Agent Tokens</div>
            </div>
            <div className="card text-center py-4">
              <div className="text-2xl font-bold text-content-primary">{organizations.length}</div>
              <div className="text-xs text-content-tertiary mt-1">Organizations</div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            {isOrgManager && (
              <Link to="/org?tab=invitations" className="card text-center py-3 hover:shadow-md transition-shadow cursor-pointer">
                <svg className="w-5 h-5 text-orbital-600 dark:text-orbital-400 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                <span className="text-xs text-content-secondary">Invite Member</span>
              </Link>
            )}
            {isOrgManager && (
              <Link to="/org?tab=devices" className="card text-center py-3 hover:shadow-md transition-shadow cursor-pointer">
                <svg className="w-5 h-5 text-orbital-600 dark:text-orbital-400 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-xs text-content-secondary">Add Device</span>
              </Link>
            )}
            <Link to="/agent-tokens" className="card text-center py-3 hover:shadow-md transition-shadow cursor-pointer">
              <svg className="w-5 h-5 text-orbital-600 dark:text-orbital-400 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              <span className="text-xs text-content-secondary">Agent Tokens</span>
            </Link>
            <Link to="/settings" className="card text-center py-3 hover:shadow-md transition-shadow cursor-pointer">
              <svg className="w-5 h-5 text-orbital-600 dark:text-orbital-400 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-xs text-content-secondary">Settings</span>
            </Link>
          </div>

          {/* Services section */}
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-content-primary mb-4">Services</h2>
          </div>

          {services.length === 0 ? (
            <div className="card text-center py-10">
              <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-content-primary mb-1">No services yet</h3>
              <p className="text-content-tertiary text-xs max-w-xs mx-auto">
                {isOrgManager
                  ? "Contact a platform administrator to enable services for your organization."
                  : "Contact your organization administrator for service access."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((svc) => (
                <div key={svc.id} className="card hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-orbital-100 dark:bg-orbital-900/40 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-orbital-600 dark:text-orbital-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-medium text-content-primary">{svc.name}</h3>
                        <p className="text-xs text-content-tertiary font-mono">{svc.id}</p>
                      </div>
                    </div>
                    <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">
                      Active
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
